import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DynamicIcon, iconNames } from 'lucide-react/dynamic';
import './IconPicker.css';

/* Visual icon picker over the full lucide set (~1,900 icons). Stores lucide's
 * kebab-case name (e.g. "building-2") — the same name the v3 coverage step
 * renders via <DynamicIcon>. Shows the icon next to its name so the admin can
 * see what they're choosing, with search to reach any icon ("add more icons").
 *
 * Only a bounded number of icons are mounted at once (a curated default set, or
 * the top matches while searching) so we never fire ~1,900 lazy imports. */

// A sensible starting palette for compliance / filing / business rows.
const SUGGESTED = [
  'building-2', 'landmark', 'briefcase', 'scale', 'gavel', 'file-text', 'file-check', 'files',
  'clipboard-check', 'clipboard-list', 'stamp', 'receipt', 'banknote', 'coins', 'wallet', 'credit-card',
  'calendar-days', 'calendar-clock', 'clock', 'bell', 'refresh-cw', 'repeat', 'shield-check', 'shield',
  'lock', 'key', 'globe', 'map-pin', 'plane', 'users', 'user-check', 'handshake',
  'mail', 'send', 'phone', 'book-open', 'graduation-cap', 'award', 'badge-check', 'check',
  'circle-check', 'percent', 'trending-up', 'chart-column', 'folder', 'archive', 'pen-line', 'signature',
];
const NAMES = new Set(iconNames);
const DEFAULT_LIST = SUGGESTED.filter((n) => NAMES.has(n));
const MAX_RESULTS = 60;

export default function IconPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return DEFAULT_LIST;
    return iconNames.filter((n) => n.includes(term)).slice(0, MAX_RESULTS);
  }, [q]);

  const pick = (name) => { onChange(name); setOpen(false); setQ(''); };

  return (
    <div className="iconpicker" ref={ref}>
      <button type="button" className="iconpicker-trigger" onClick={() => setOpen((o) => !o)}>
        <span className="iconpicker-preview">
          <DynamicIcon name={value || 'check'} size={16} fallback={() => <span className="iconpicker-dot" />} />
        </span>
        <span className="iconpicker-name">{value || 'check'}</span>
        <span className="iconpicker-caret">▾</span>
      </button>

      {open && (
        <div className="iconpicker-pop">
          <input
            className="form-input iconpicker-search"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 1,900+ icons…"
          />
          <div className="iconpicker-grid">
            {results.length === 0 && <div className="iconpicker-empty">No icons match “{q}”.</div>}
            {results.map((name) => (
              <button
                type="button"
                key={name}
                className={`iconpicker-cell${name === value ? ' active' : ''}`}
                title={name}
                onClick={() => pick(name)}
              >
                <DynamicIcon name={name} size={18} fallback={() => <span className="iconpicker-dot" />} />
                <span className="iconpicker-cell-name">{name}</span>
              </button>
            ))}
          </div>
          {!q.trim() && <div className="iconpicker-hint">Showing common icons — type to search the full set.</div>}
        </div>
      )}
    </div>
  );
}
